'use client'

import { motion } from 'motion/react'
import Link, { useLinkStatus } from 'next/link'
import { useLocale } from 'next-intl'
import { ReactNode } from 'react'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Locale } from '@/setup/locales'
import { getLinkHref, ILinkParams } from '@/setup/routing'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

export interface INavigationMobileItemProps {
    linkParams: ILinkParams | { externalUrl: string; target?: '_blank' | '_self' }
    label: ReactNode
    isActive: boolean
    gtmId?: string
}

const NavigationMobileItemContent = ({
    label,
    isActive,
}: Pick<INavigationMobileItemProps, 'label' | 'isActive'>) => {
    const { pending } = useLinkStatus()

    return (
        <motion.div
            initial={{
                backgroundColor: isActive ? 'rgba(0, 0, 0, 0.07)' : 'rgba(0, 0, 0, 0)',
            }}
            animate={{
                backgroundColor: isActive ? 'rgba(0, 0, 0, 0.07)' : 'rgba(0, 0, 0, 0)',
            }}
            whileTap={{
                backgroundColor: isActive ? 'rgba(0, 0, 0, 0.07)' : 'rgba(0, 0, 0, 0.02)',
            }}
            transition={{
                backgroundColor: { duration: 0, ease: 'easeOut' },
            }}
            className={cn(
                `flex items-center px-8 py-[8px] font-semibold relative h-[40px] min-h-[64px] rounded-l
                select-none w-full touch-none`,
                pending ? 'justify-between' : 'justify-start'
            )}
        >
            {label}
            {pending && <LoadingSpinner size={24} color={COLORS.black} />}
        </motion.div>
    )
}

export const NavigationMobileItem = ({
    linkParams,
    label,
    isActive,
    gtmId,
}: INavigationMobileItemProps) => {
    const locale = useLocale()

    if ('externalUrl' in linkParams) {
        return (
            <a className={gtmId} href={linkParams.externalUrl} target={linkParams.target}>
                <NavigationMobileItemContent label={label} isActive={isActive} />
            </a>
        )
    }

    return (
        <Link href={getLinkHref(linkParams, locale as Locale)} className={gtmId}>
            <NavigationMobileItemContent label={label} isActive={isActive} />
        </Link>
    )
}
